import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';

/** A vehicle registered on a family plan, active or removed. */
export interface FamilyPlanVehicle {
  vehicle_id: string;
  license_plate: string;
  state: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  status: 'active' | 'removed';
  added_at: string | null;
  added_by_name: string | null;
  removed_at: string | null;
  removed_by_name: string | null;
  last_wash_at: string | null;
}

/** A customer's family plan membership with its registered vehicles. */
export interface FamilyPlanMembership {
  subscription_id: string;
  order_id: string | null;
  package_id: string;
  package_name: string;
  price: string | number | null;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  created_at: string | null;
  vehicle_limit: number;
  active_vehicle_count: number;
  can_manage_vehicles: boolean;
  /** The vehicle the plan was bought for, when known. */
  original_vehicle_id?: string | null;
  vehicles: FamilyPlanVehicle[];
}

export interface AddFamilyPlanVehicleRequest {
  license_plate: string;
  state: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  acknowledge_shared_vehicle?: boolean;
}

export interface StopRenewalResult {
  cancel_at_period_end: boolean;
  already_scheduled?: boolean;
  access_until?: string | null;
  current_period_end?: string | null;
}

/** Error codes the family plan API returns in `data.code`. */
export const FAMILY_PLAN_ERROR_CODES = {
  VEHICLE_LIMIT_REACHED: 'VEHICLE_LIMIT_REACHED',
  VEHICLE_ALREADY_ON_PLAN: 'VEHICLE_ALREADY_ON_PLAN',
  VEHICLE_ON_ANOTHER_FAMILY_PLAN: 'VEHICLE_ON_ANOTHER_FAMILY_PLAN',
  LAST_VEHICLE: 'LAST_VEHICLE',
} as const;

/** Family plan vehicle management and renewal for company admins (CWS-1231). */
@Injectable({
  providedIn: 'root',
})
export class FamilyPlanService {
  constructor(private readonly apiService: ApiService) {}

  getCustomerFamilyPlans(customerId: string): Observable<FamilyPlanMembership[]> {
    return this.apiService
      .get<FamilyPlanMembership[]>(`order/family-plan/customer/${customerId}`)
      .pipe(map(response => response.data ?? []));
  }

  addVehicle(
    subscriptionId: string,
    request: AddFamilyPlanVehicleRequest
  ): Observable<FamilyPlanMembership | null> {
    return this.apiService
      .post<FamilyPlanMembership>(`order/family-plan/${subscriptionId}/vehicles`, request)
      .pipe(map(response => response.data ?? null));
  }

  removeVehicle(
    subscriptionId: string,
    vehicleId: string
  ): Observable<FamilyPlanMembership | null> {
    return this.apiService
      .delete<FamilyPlanMembership>(`order/family-plan/${subscriptionId}/vehicles/${vehicleId}`)
      .pipe(map(response => response.data ?? null));
  }

  stopRenewal(orderId: string, reason?: string): Observable<StopRenewalResult | null> {
    return this.apiService
      .put<StopRenewalResult>(`order/family-plan/orders/${orderId}/stop-renewal`, {
        cancellation_reason: reason || null,
      })
      .pipe(map(response => response.data ?? null));
  }
}

/** The API's message and `data.code` from a failed family plan request. */
export function familyPlanError(
  error: unknown,
  fallback: string
): { message: string; code: string | null; data: Record<string, unknown> | null } {
  const body = error instanceof HttpErrorResponse ? error.error : null;
  // Validation failures arrive as { errors: { field: message } } with no top-level message.
  const validationMessage =
    body?.errors && typeof body.errors === 'object'
      ? Object.values(body.errors as Record<string, string>)[0]
      : null;
  return {
    message: body?.message || validationMessage || fallback,
    code: body?.data?.code ?? null,
    data: body?.data ?? null,
  };
}
